import { useEffect, useState } from 'react';
import { selectActiveNodes, selectActiveEdges, useStore } from '../store';

export function StatusBar() {
  const nodes = useStore(selectActiveNodes);
  const edges = useStore(selectActiveEdges);
  const nodeCount = nodes.length;
  const edgeCount = edges.length;
  const selectedCount =
    nodes.filter((n) => n.selected).length + edges.filter((e) => e.selected).length;
  const snapToGrid = useStore((s) => s.snapToGrid);
  // Re-attach when the active sheet changes — the canvas (and its React-Flow
  // pane) is torn down and re-mounted per sheet, so a one-time mount listener
  // would go stale (this was the "X/Y stuck at ––" bug).
  const activeSheetId = useStore((s) => s.activeSheetId);

  // Track cursor in flow coordinates via a document-level listener on the canvas.
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);
  const [zoomPct, setZoomPct] = useState(100);

  useEffect(() => {
    let pane: HTMLElement | null = null;
    let viewport: HTMLElement | null = null;
    let ro: MutationObserver | null = null;
    let raf = 0;
    let tries = 0;

    const parseTransform = (vp: HTMLElement) => {
      const m = /matrix\(([^)]+)\)|translate\(([^)]+)\)\s*scale\(([^)]+)\)/.exec(vp.style.transform);
      if (!m) return { x: 0, y: 0, k: 1 };
      if (m[1]) {
        const parts = m[1].split(',').map((s) => parseFloat(s.trim()));
        return { x: parts[4] ?? 0, y: parts[5] ?? 0, k: parts[0] ?? 1 };
      }
      const [tx, ty] = (m[2] ?? '0,0').split(',').map((s) => parseFloat(s.trim()));
      return { x: tx, y: ty, k: parseFloat(m[3] ?? '1') };
    };

    const onMove = (e: MouseEvent) => {
      if (!pane || !viewport) return;
      const rect = pane.getBoundingClientRect();
      const { x, y, k } = parseTransform(viewport);
      setCursor({ x: Math.round((e.clientX - rect.left - x) / k), y: Math.round((e.clientY - rect.top - y) / k) });
      setZoomPct(Math.round(k * 100));
    };

    // The pane mounts a frame or two after we run; retry a bounded number of
    // times, then give up (non-graph sheets have no pane — that's fine).
    const attach = () => {
      pane = document.querySelector<HTMLElement>('.react-flow__pane');
      viewport = document.querySelector<HTMLElement>('.react-flow__viewport');
      if (!pane || !viewport) {
        if (tries++ < 30) raf = requestAnimationFrame(attach);
        return;
      }
      pane.addEventListener('mousemove', onMove);
      ro = new MutationObserver(() => {
        if (viewport) setZoomPct(Math.round(parseTransform(viewport).k * 100));
      });
      ro.observe(viewport, { attributes: true, attributeFilter: ['style'] });
      setZoomPct(Math.round(parseTransform(viewport).k * 100));
    };
    attach();

    return () => {
      cancelAnimationFrame(raf);
      pane?.removeEventListener('mousemove', onMove);
      ro?.disconnect();
      setCursor(null);
    };
  }, [activeSheetId]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '4px 14px',
        background: 'var(--panel)',
        borderTop: '1px solid var(--panel-border)',
        fontSize: 11,
        color: 'var(--text-muted)',
        fontFamily: 'ui-monospace, Consolas, monospace',
        height: 24,
        boxSizing: 'border-box',
      }}
    >
      <span style={{ minWidth: 140 }}>
        {cursor ? (
          <>
            <span style={{ color: 'var(--text-dim)' }}>X:</span>{' '}
            {cursor.x.toString().padStart(5, ' ')}{' '}
            <span style={{ color: 'var(--text-dim)' }}>Y:</span>{' '}
            {cursor.y.toString().padStart(5, ' ')}
          </>
        ) : (
          <span style={{ color: 'var(--text-dim)' }}>X:    ––  Y:    ––</span>
        )}
      </span>
      <Divider />
      <span>
        <span style={{ color: 'var(--text-dim)' }}>Zoom:</span> {zoomPct}%
      </span>
      <Divider />
      <span>
        <span style={{ color: 'var(--text-dim)' }}>Grid:</span> {snapToGrid ? '20 px (snap)' : 'off'}
      </span>
      <Divider />
      <span>
        <span style={{ color: 'var(--text-dim)' }}>Components:</span> {nodeCount}
      </span>
      <span>
        <span style={{ color: 'var(--text-dim)' }}>Wires:</span> {edgeCount}
      </span>
      {selectedCount > 0 && (
        <>
          <Divider />
          <span style={{ color: 'var(--accent)' }}>
            {selectedCount} selected
          </span>
        </>
      )}
      <div style={{ flex: 1 }} />
    </div>
  );
}

function Divider() {
  return (
    <div
      style={{
        width: 1,
        height: 12,
        background: 'var(--panel-border-strong)',
      }}
    />
  );
}
